import { MaskLine, Rise } from "@/components/motion/Reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/seo/Seo";
import { ldBreadcrumb } from "@/seo/seoConfig";

const SECTIONS = [
  {
    n: "01",
    title: "Who we are",
    body: [
      "This Privacy & Data Protection Notice applies to the website operated by Rane Pingle and Partners FZ LLC (\"RPLC UAE\", \"we\", \"our\"), a free-zone company registered in the RAKEZ Business Zone, Al Nakheel Area, P.O. Box No. 10055, Ras Al Khaimah, United Arab Emirates.",
      "Under this notice, RPLC UAE is the data controller for personal data collected via www.rplcuae.com and www.rplawchambers.com. If you have any privacy question or wish to exercise a right, write to us at chambers@rplc.ae.",
    ],
  },
  {
    n: "02",
    title: "What we collect",
    body: [
      "Contact form data — your name, email address, business name (optional) and message. Provided voluntarily when you submit the RPLC UAE contact form.",
      "Technical data — IP address, device and browser identifiers, referrer, and pages viewed. Collected automatically for site security and, with your consent, for aggregate analytics.",
      "Cookie preferences — a record of whether you accepted or rejected non-essential cookies. Stored locally in your browser.",
    ],
  },
  {
    n: "03",
    title: "How we use it",
    body: [
      "To respond to your enquiry and to consider whether we can act for you consistent with our professional obligations, including conflict checks and applicable Bar Council rules.",
      "To secure and operate the website (necessary cookies, error logging, abuse prevention).",
      "With your consent, to understand aggregate usage of the site via PostHog analytics.",
      "We do not sell or share personal data for third-party marketing purposes.",
    ],
  },
  {
    n: "04",
    title: "Legal bases",
    body: [
      "UAE Federal Decree-Law No. 45 of 2021 (Personal Data Protection Law) — legitimate interests for necessary site operation; consent for analytics and marketing cookies; performance of a legal service where you have engaged us.",
      "India Digital Personal Data Protection Act, 2023 — consent for enquiry processing; legitimate uses for security and dispute prevention.",
      "EU / UK GDPR (for EU/UK visitors) — Articles 6(1)(b), 6(1)(c), 6(1)(f) and, for optional cookies, 6(1)(a).",
    ],
  },
  {
    n: "05",
    title: "Cross-border transfers",
    body: [
      "Enquiry data may be processed in the UAE and India by the RPLC team. Where required, we rely on Standard Contractual Clauses and equivalent safeguards under the UAE PDPL and DPDPA.",
      "We do not transfer personal data to jurisdictions without an adequate level of protection unless supplementary measures are in place.",
    ],
  },
  {
    n: "06",
    title: "Retention",
    body: [
      "Enquiry emails and messages are retained for as long as required to consider your matter and, thereafter, for the applicable limitation period under UAE and Indian law.",
      "Cookie preferences are retained locally in your browser until you clear them or reset preferences.",
    ],
  },
  {
    n: "07",
    title: "Your rights",
    body: [
      "You have the right to access, correct, restrict, or request deletion of your personal data, and to withdraw consent for optional processing at any time.",
      "You may lodge a complaint with the UAE Data Office, the Data Protection Board of India, or an EU supervisory authority (as applicable).",
      "To exercise a right, write to chambers@rplc.ae with sufficient detail to identify the data concerned.",
    ],
  },
  {
    n: "08",
    title: "Cookies",
    body: [
      "Strictly necessary cookies are always on and are used to run the site, remember your language, and prevent abuse.",
      "Analytics cookies (PostHog) run only after you accept them via the cookie banner.",
      "Marketing cookies are off by default. You can change your preferences at any time using the ‘Manage preferences’ option in the cookie banner.",
    ],
  },
  {
    n: "09",
    title: "Bar Council notice",
    body: [
      "As per the rules of the Bar Council of India, this website does not solicit work or advertise. By using it, you confirm you are accessing our information at your own volition; no lawyer-client relationship is created by any transmission from this site.",
      "Regulated activities and formal representation before UAE authorities are undertaken, where required, through appropriately licensed professionals and collaboration partners.",
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div className="pt-40">
      <Seo
        title="Privacy & Data Protection · UAE PDPL · India DPDPA · GDPR"
        description="RPLC UAE's Privacy & Data Protection Notice — a plain-language guide aligned with UAE PDPL, India's DPDPA and the EU/UK GDPR. Cookie preferences, legal bases and cross-border transfer position."
        path="/privacy"
        keywords={["UAE PDPL privacy policy", "India DPDPA policy", "GDPR privacy notice", "law firm privacy policy Dubai"]}
        jsonLd={ldBreadcrumb([{ name: "Home", path: "/" }, { name: "Privacy", path: "/privacy" }])}
      />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Rise>
          <div className="flex items-center gap-4 mb-10">
            <span className="accent-bar" />
            <span className="eyebrow">Privacy & Data Protection · v1 · {new Date().toLocaleString("en-GB", { month: "short", year: "numeric" })}</span>
          </div>
        </Rise>
        <h1 className="font-serif tracking-tight leading-[0.9] text-[13vw] lg:text-[8vw]">
          <MaskLine delay={0.15}>Data,</MaskLine>
          <MaskLine delay={0.32}><span className="italic-accent">respectfully</span> handled.</MaskLine>
        </h1>
        <Rise delay={0.5}>
          <p className="mt-10 text-[#A19D94] text-lg max-w-3xl leading-relaxed">
            A plain-language explanation of what we collect, why we collect it, and the rights you have — under the UAE PDPL, India&rsquo;s DPDPA and the EU/UK GDPR.
          </p>
        </Rise>
      </section>

      <article className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <div className="chapter-num text-[#66635C] mb-4">Contents</div>
              <ol className="space-y-3">
                {SECTIONS.map((s) => (
                  <li key={s.n} className="flex gap-3 text-sm">
                    <span className="chapter-num text-[#66635C]">{s.n}</span>
                    <a href={`#s-${s.n}`} className="text-[#A19D94] link-hover">{s.title}</a>
                  </li>
                ))}
              </ol>
              <div className="hairline my-8" />
              <div className="chapter-num text-[#66635C] mb-2">Written to</div>
              <a href="mailto:chambers@rplc.ae" className="text-[#F4F1EB] link-hover">chambers@rplc.ae</a>
            </div>
          </aside>

          <div className="lg:col-span-9 space-y-16">
            {SECTIONS.map((s, i) => (
              <Rise key={s.n} id={`s-${s.n}`} delay={i * 0.03}>
                <div id={`s-${s.n}`} className="grid grid-cols-12 gap-6 pb-14 border-b border-[#141414]">
                  <div className="col-span-2 lg:col-span-1 chapter-num text-[#C84C35] pt-2">{s.n}</div>
                  <div className="col-span-10 lg:col-span-11">
                    <h2 className="font-serif text-3xl lg:text-4xl tracking-tight leading-tight">{s.title}</h2>
                    <div className="mt-6 space-y-5">
                      {s.body.map((b, k) => (
                        <p key={k} className="text-[#A19D94] text-base lg:text-lg leading-relaxed">{b}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </article>

      <section className="py-24 border-t border-[#141414] mt-16">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Rise>
              <h2 className="font-serif text-5xl lg:text-[6vw] leading-[0.95] tracking-tight">
                A privacy <span className="italic-accent">question?</span>
              </h2>
            </Rise>
          </div>
          <div className="lg:col-span-4 lg:justify-end flex">
            <Rise delay={0.1}>
              <Link to="/contact" className="btn-primary">
                Write to us <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </Rise>
          </div>
        </div>
      </section>
    </div>
  );
}
